// src/components/ChatDialog.jsx

import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  IconButton
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * ChatDialog
 * 
 * Exibe um diálogo para troca de mensagens (chat) com visual aprimorado.
 * 
 * Props:
 *  - open: (bool) se o diálogo está aberto
 *  - onClose: (func) função para fechar o diálogo
 *  - context: (string) 'projeto' ou 'monografia', indicando o contexto da conversa
 *  - contextId: (number) ID do projeto ou monografia
 *  - currentUser: (string) nome do usuário logado (usado para identificar o remetente)
 *  - title: (string, opcional) título customizado para o diálogo; se não for passado, usa um título padrão
 *  - messages: (array) array com o histórico de mensagens. Cada mensagem deve ter pelo menos: sender, message e created_at.
 *  - loading: (bool) indica se o histórico está sendo carregado
 *  - onSendMessage: (func) função a ser chamada ao clicar em "Enviar"
 *  - message: (string) valor atual do campo de mensagem
 *  - onMessageChange: (func) função para atualizar o campo de mensagem
 *  - onRefresh: (func, opcional) função para atualizar o histórico de mensagens (usada no botão de refresh)
 */
const ChatDialog = ({
  open,
  onClose,
  context,
  contextId,
  currentUser,
  title,
  messages,
  loading,
  onSendMessage,
  message,
  onMessageChange,
  onRefresh
}) => {
  // Define o título: se a prop "title" for fornecida, ela é usada; caso contrário, cria um título padrão.
  const dialogTitle = title || `Chat com ${context} #${contextId}`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {/* Título do diálogo com o botão de refresh posicionado no canto superior direito */}
      <DialogTitle sx={{ position: 'relative' }}>
        {dialogTitle}
        {onRefresh && (
          <IconButton 
            onClick={onRefresh}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="small"
            aria-label="atualizar"
          >
            <RefreshIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          // Exibe um indicador de carregamento enquanto o histórico não estiver disponível
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          // Container do histórico de mensagens com rolagem, borda e padding
          <Box
            sx={{
              maxHeight: 300,
              overflowY: 'auto',
              mb: 2,
              p: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}
          >
            {messages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Sem mensagens ainda.
              </Typography>
            ) : (
              messages.map((msg, index) => {
                // Define se a mensagem foi enviada pelo usuário atual para ajustar o alinhamento e as cores
                const isCurrentUser = msg.sender === currentUser;
                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
                      mb: 1
                    }}
                  >
                    {/* Exibe o timestamp da mensagem */}
                    <Typography variant="caption" color="text.secondary">
                      {new Date(msg.created_at).toLocaleString()}
                    </Typography>
                    {/* Balão (bubble) da mensagem */}
                    <Box
                      sx={{
                        backgroundColor: isCurrentUser ? 'primary.light' : 'grey.300',
                        color: isCurrentUser ? 'primary.contrastText' : 'text.primary',
                        borderRadius: 2,
                        p: 1,
                        mt: 0.5,
                        maxWidth: '80%'
                      }}
                    >
                      <Typography variant="body2">
                        <strong>{msg.sender}:</strong> {msg.message}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        )}
        {/* Campo de entrada para a mensagem */}
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Digite sua mensagem..."
          value={message}
          onChange={onMessageChange}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button onClick={onSendMessage} variant="contained">
          Enviar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ChatDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  context: PropTypes.oneOf(['projeto', 'monografia']).isRequired,
  contextId: PropTypes.number.isRequired,
  currentUser: PropTypes.string.isRequired,
  title: PropTypes.string,
  messages: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  message: PropTypes.string.isRequired,
  onMessageChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func
};

export default ChatDialog;
